import React from 'react';
import { Map, BookOpen, Ticket, Compass, Fish, Command } from 'lucide-react';
import { ThemeToggle } from '../Shared/ThemeToggle';
import { useApp } from '../../context/AppContext';
import { motion } from 'framer-motion';
type NavItem = 'map' | 'regulations' | 'permits' | 'recommendations';
interface SidebarProps {
  activeTab: NavItem;
  onTabChange: (tab: NavItem) => void;
}
export function Sidebar({
  activeTab,
  onTabChange
}: SidebarProps) {
  const {
    catches
  } = useApp();
  const navItems = [{
    id: 'map',
    icon: Map,
    label: 'Explore',
    shortcut: '1'
  }, {
    id: 'recommendations',
    icon: Compass,
    label: 'For You',
    shortcut: '2'
  }, {
    id: 'permits',
    icon: Ticket,
    label: 'Permits',
    shortcut: '3'
  }, {
    id: 'regulations',
    icon: BookOpen,
    label: 'Rules',
    shortcut: '4'
  }] as const;
  return <aside className="hidden md:flex flex-col w-64 h-screen fixed left-0 top-0 z-50 bg-[var(--bg-card)] border-r border-[var(--border)]" data-id="element-134">
      {/* Logo */}
      <div className="h-16 px-5 flex items-center border-b border-[var(--border)]" data-id="element-135">
        <div className="w-8 h-8 rounded-lg bg-[var(--accent)] flex items-center justify-center" data-id="element-136">
          <Fish className="w-4 h-4 text-white" data-id="element-137" />
        </div>
        <span className="ml-3 text-base font-semibold text-[var(--text-primary)] tracking-tight" data-id="element-138">
          Angler
        </span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3" data-id="element-139">
        <div className="space-y-0.5" data-id="element-140">
          {navItems.map(item => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return <button key={item.id} onClick={() => onTabChange(item.id)} className="relative flex items-center w-full h-11 px-3 rounded-lg group transition-colors" data-id="element-141">
                {/* Active background */}
                {isActive && <motion.div layoutId="sidebar-active" className="absolute inset-0 bg-[var(--accent)] rounded-lg" transition={{
              type: 'spring',
              stiffness: 500,
              damping: 35
            }} data-id="element-142" />}

                {/* Content */}
                <div className="relative flex items-center justify-between w-full" data-id="element-143">
                  <div className="flex items-center" data-id="element-144">
                    <Icon className={`w-[18px] h-[18px] transition-colors ${isActive ? 'text-white' : 'text-[var(--text-muted)] group-hover:text-[var(--text-primary)]'}`} strokeWidth={2} data-id="element-145" />
                    <span className={`ml-3 text-sm transition-colors ${isActive ? 'text-white font-medium' : 'text-[var(--text-secondary)] group-hover:text-[var(--text-primary)]'}`} data-id="element-146">
                      {item.label}
                    </span>
                  </div>

                  {/* Keyboard shortcut */}
                  <kbd className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${isActive ? 'bg-white/20 text-white/70' : 'bg-[var(--bg-secondary)] text-[var(--text-muted)] opacity-0 group-hover:opacity-100'}`} data-id="element-147">
                    {item.shortcut}
                  </kbd>
                </div>
              </button>;
        })}
        </div>
      </nav>

      {/* Stats */}
      {catches.length > 0 && <div className="mx-3 mb-3 px-3 py-2.5 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-subtle)]" data-id="element-148">
          <div className="flex items-center justify-between" data-id="element-149">
            <span className="text-xs text-[var(--text-muted)]" data-id="element-150">
              Catches logged
            </span>
            <span className="text-sm font-semibold text-[var(--accent)]" data-id="element-151">
              {catches.length}
            </span>
          </div>
        </div>}

      {/* Footer */}
      <div className="p-3 border-t border-[var(--border)]" data-id="element-152">
        <div className="flex items-center justify-between px-3 py-2" data-id="element-153">
          <div className="flex items-center" data-id="element-154">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center text-white text-xs font-semibold" data-id="element-155">
              J
            </div>
            <div className="ml-2.5" data-id="element-156">
              <p className="text-sm font-medium text-[var(--text-primary)] leading-none" data-id="element-157">
                John
              </p>
              <p className="text-[10px] text-[var(--text-muted)] mt-0.5" data-id="element-158">Pro</p>
            </div>
          </div>
          <ThemeToggle data-id="element-159" />
        </div>
      </div>
    </aside>;
}